/**
 * Authentication Context
 * Provides authentication state management across the application
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AuthContextType, LoginCredentials, AuthResponse, UserProfile } from '../types';
import { sessionManager } from '../services/sessionManager';

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props interface for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides login functionality
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

  /**
   * Initialize authentication state from stored session
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize session manager
        sessionManager.initialize();

        // Check for existing valid session
        const sessionData = sessionManager.getSessionData();
        if (sessionData?.user) {
          // Validate token with real API
          try {
            const response = await fetch(`${API_URL}/auth/validate-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionData.token}`
              }
            });

            const contentType = response.headers.get("content-type");
            if (contentType?.includes("application/json")) {
              const authResponse = await response.json();
              if (response.ok && authResponse.success) {
                // Restore user session
                setUser(sessionData.user);
              } else {
                sessionManager.clearSession();
              }
            } else {
              // Non-JSON response (e.g. 502), treat as invalid session
              console.warn('Token validation received non-JSON response');
              sessionManager.clearSession();
            }
          } catch (err) {
            console.warn('Token validation failed:', err);
            // Optionally clear session on network error or keep it? 
            // For security, maybe clear if we can't validate.
            // But for offline support, maybe keep. 
            // Let's clear for now to be safe.
            sessionManager.clearSession();
          }
        }
      } catch (error) {
        console.warn('Failed to initialize authentication:', error);
        sessionManager.clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   * Authenticates user and establishes session
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResponse> => {
    setIsLoading(true);

    try {
      // Authenticate with real API
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const contentType = response.headers.get("content-type");
      let authResponse: AuthResponse;

      if (contentType?.includes("application/json")) {
        authResponse = await response.json();
      } else {
        const text = await response.text();
        console.error("Non-JSON login response:", text);

        let errorMessage = "An unexpected error occurred during login";
        if (response.status === 502 || response.status === 503) {
          errorMessage = "Service is temporarily unavailable. Please try again later.";
        } else if (response.status === 500) {
          errorMessage = "Internal Server Error. Please try again later.";
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      if (response.ok && authResponse.success && authResponse.token && authResponse.user) {
        // Create and store session data
        const sessionData = sessionManager.createSessionData(
          authResponse.token,
          authResponse.user
        );
        sessionManager.setSessionData(sessionData);

        // Update authentication state
        setUser(authResponse.user);
      }

      return authResponse;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during login'
      };
    } finally {
      setIsLoading(false);
    }
  }, [API_URL]);



  /**
   * Check if user is authenticated
   */
  const isAuthenticated = user !== null;

  // Context value
  const contextValue: AuthContextType = useMemo(() => ({
    user,
    login,
    isAuthenticated,
    isLoading
  }), [user, login, isAuthenticated, isLoading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 * Provides type-safe access to authentication state and methods
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

/**
 * Higher-order component for components that require authentication
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return <div>Access denied. Please log in.</div>;
    }

    return <Component {...props} />;
  };
};